import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  LinearProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PhotoLibrary,
  Send,
} from '@mui/icons-material';
import { usePosts } from '../../context/posts.context';
import { useSnackbar } from '../../context/snackbar.context';

const EMPTY_STAT = { label: '', value: '', change: '' };

/**
 * Dialog for creating a progress photo post.
 * Upload before/after images, add stats changes, milestone, and caption.
 */
const CreateProgressPost = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { createPost, submitting, progressPostOpen, closeProgressPost } = usePosts();
  const { showSnackbar } = useSnackbar();

  const [error, setError] = useState(null);

  // Progress fields
  const [progressTitle, setProgressTitle] = useState('');
  const [milestone, setMilestone] = useState('');
  const [caption, setCaption] = useState('');
  const [stats, setStats] = useState([{ ...EMPTY_STAT }]);

  // Before image
  const [beforeFile, setBeforeFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const beforeInputRef = useRef(null);

  // After image
  const [afterFile, setAfterFile] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const afterInputRef = useRef(null);

  const [uploadProgress, setUploadProgress] = useState(0);

  const canSubmit = progressTitle.trim().length > 0 && (beforeFile || afterFile) && !submitting;

  // --- Stats management ---
  const handleStatChange = (index, field, value) => {
    setStats((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddStat = () => {
    setStats((prev) => [...prev, { ...EMPTY_STAT }]);
  };

  const handleRemoveStat = (index) => {
    setStats((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Image handling ---
  const handleImageSelect = useCallback((event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please select a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image is too large. Maximum size is 5MB.');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'before') {
        setBeforeFile(file);
        setBeforePreview(e.target.result);
      } else {
        setAfterFile(file);
        setAfterPreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const handleRemoveBeforeImage = useCallback(() => {
    setBeforeFile(null);
    setBeforePreview(null);
  }, []);

  const handleRemoveAfterImage = useCallback(() => {
    setAfterFile(null);
    setAfterPreview(null);
  }, []);

  // --- Submit ---
  const handleSubmit = useCallback(async () => {
    setError(null);
    setUploadProgress(0);
    try {
      const validStats = stats.filter((s) => s.label.trim() && s.value.trim());
      const progressData = {
        title: progressTitle.trim(),
        beforeImageUrl: null,
        afterImageUrl: null,
        stats: validStats.map((s) => ({
          label: s.label.trim(),
          value: s.value.trim(),
          change: s.change.trim() || null,
        })),
        milestone: milestone.trim() || null,
      };

      await createPost({
        type: 'progress',
        text: caption.trim(),
        onImageProgress: (progress) => setUploadProgress(progress),
        progress: progressData,
        beforeImageFile: beforeFile || undefined,
        afterImageFile: afterFile || undefined,
      });

      resetForm();
      closeProgressPost();
      showSnackbar('Progress shared!', 'success');
    } catch (err) {
      setError(err.message || 'Failed to create progress post.');
    }
  }, [progressTitle, milestone, caption, stats, beforeFile, afterFile, createPost, closeProgressPost, showSnackbar]);

  const resetForm = () => {
    setProgressTitle('');
    setMilestone('');
    setCaption('');
    setStats([{ ...EMPTY_STAT }]);
    setBeforeFile(null);
    setBeforePreview(null);
    setAfterFile(null);
    setAfterPreview(null);
    setUploadProgress(0);
    setError(null);
  };

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetForm();
    closeProgressPost();
  }, [submitting, closeProgressPost]);

  return (
    <Dialog
      open={progressPostOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      aria-labelledby="create-progress-dialog-title"
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={0}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} id="create-progress-dialog-title">
          Progress Photo
        </Typography>
        <IconButton edge="end" onClick={handleClose} disabled={submitting} aria-label="Close dialog">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Title */}
        <TextField
          label="Title"
          placeholder='e.g., "Month 3 Check-in"'
          value={progressTitle}
          onChange={(e) => setProgressTitle(e.target.value)}
          fullWidth
          autoFocus
          sx={{ mb: 2 }}
        />

        {/* Before/After images */}
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
          Photos
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {/* Before */}
          <Box sx={{ flex: 1 }}>
            <input
              ref={beforeInputRef}
              hidden
              accept="image/jpeg,image/png,image/gif,image/webp"
              type="file"
              onChange={(e) => handleImageSelect(e, 'before')}
            />
            {beforePreview ? (
              <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  component="img"
                  src={beforePreview}
                  alt="Before preview"
                  sx={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 2, display: 'block' }}
                />
                <IconButton
                  onClick={handleRemoveBeforeImage}
                  disabled={submitting}
                  sx={{
                    position: 'absolute', top: 4, right: 4,
                    backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                  }}
                  size="small"
                  aria-label="Remove before image"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute', bottom: 4, left: 4,
                    backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                    px: 0.75, py: 0.25, borderRadius: 0.5, fontWeight: 600,
                  }}
                >
                  Before
                </Typography>
              </Box>
            ) : (
              <Button
                onClick={() => beforeInputRef.current?.click()}
                variant="outlined"
                fullWidth
                sx={{
                  height: 150,
                  textTransform: 'none',
                  flexDirection: 'column',
                  gap: 0.5,
                  borderStyle: 'dashed',
                }}
              >
                <PhotoLibrary />
                Before
              </Button>
            )}
          </Box>

          {/* After */}
          <Box sx={{ flex: 1 }}>
            <input
              ref={afterInputRef}
              hidden
              accept="image/jpeg,image/png,image/gif,image/webp"
              type="file"
              onChange={(e) => handleImageSelect(e, 'after')}
            />
            {afterPreview ? (
              <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  component="img"
                  src={afterPreview}
                  alt="After preview"
                  sx={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 2, display: 'block' }}
                />
                <IconButton
                  onClick={handleRemoveAfterImage}
                  disabled={submitting}
                  sx={{
                    position: 'absolute', top: 4, right: 4,
                    backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                  }}
                  size="small"
                  aria-label="Remove after image"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute', bottom: 4, left: 4,
                    backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                    px: 0.75, py: 0.25, borderRadius: 0.5, fontWeight: 600,
                  }}
                >
                  After
                </Typography>
              </Box>
            ) : (
              <Button
                onClick={() => afterInputRef.current?.click()}
                variant="outlined"
                fullWidth
                sx={{
                  height: 150,
                  textTransform: 'none',
                  flexDirection: 'column',
                  gap: 0.5,
                  borderStyle: 'dashed',
                }}
              >
                <PhotoLibrary />
                After
              </Button>
            )}
          </Box>
        </Box>

        {/* Stats */}
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
          Stats Changes
        </Typography>
        {stats.map((stat, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField
              label="Label"
              placeholder="Weight"
              value={stat.label}
              onChange={(e) => handleStatChange(index, 'label', e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Value"
              placeholder="175 lbs"
              value={stat.value}
              onChange={(e) => handleStatChange(index, 'value', e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Change"
              placeholder="-15 lbs"
              value={stat.change}
              onChange={(e) => handleStatChange(index, 'change', e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            {stats.length > 1 && (
              <IconButton
                size="small"
                onClick={() => handleRemoveStat(index)}
                aria-label="Remove stat"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddStat}
          sx={{ textTransform: 'none', mb: 2 }}
        >
          Add Stat
        </Button>

        {/* Milestone */}
        <TextField
          label="Milestone (optional)"
          placeholder='e.g., "First 10lbs lost!"'
          value={milestone}
          onChange={(e) => setMilestone(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Caption */}
        <TextField
          label="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 1 }}
        />

        {/* Upload progress */}
        {submitting && (beforeFile || afterFile) && uploadProgress > 0 && uploadProgress < 100 && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="text.secondary">
              Uploading images... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <Box display="flex" justifyContent="flex-end" p={2}>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="contained"
          endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Send />}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {submitting ? 'Posting...' : 'Share Progress'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CreateProgressPost;
